import React, { useEffect, useRef, useState } from 'react';
import { Maximize2, Minimize2, Crosshair } from 'lucide-react';

export default function MapView({ alerts, selectedAlert, onSelectAlert, liveLocations = {} }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef({});
  const trailsRef = useRef({});
  const trailPointsRef = useRef({});
  const [leafletReady, setLeafletReady] = useState(!!window.L);
  const [fullscreen, setFullscreen] = useState(false);

  // Wait for Leaflet to load if it's not ready yet
  useEffect(() => {
    if (window.L) { setLeafletReady(true); return; }
    const interval = setInterval(() => {
      if (window.L) { setLeafletReady(true); clearInterval(interval); }
    }, 100);
    return () => clearInterval(interval);
  }, []);

  // Initialize map
  useEffect(() => {
    if (!leafletReady || !mapRef.current || mapInstance.current) return;
    const L = window.L;

    mapInstance.current = L.map(mapRef.current, {
      center: [24.4539, 54.3773], // Abu Dhabi default
      zoom: 11,
      zoomControl: false,
      attributionControl: false
    });

    L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
      maxZoom: 19,
    }).addTo(mapInstance.current);

    L.control.zoom({ position: 'topright' }).addTo(mapInstance.current);

    // Force a resize after init (fixes blank map when in flex layout)
    setTimeout(() => mapInstance.current?.invalidateSize(), 100);

    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, [leafletReady]);

  // Re-fit when fullscreen toggles
  useEffect(() => {
    if (mapInstance.current) {
      setTimeout(() => mapInstance.current.invalidateSize(), 350);
    }
  }, [fullscreen]);

  // Update markers
  useEffect(() => {
    const L = window.L;
    if (!L || !mapInstance.current) return;
    const map = mapInstance.current;

    const currentIds = new Set(alerts.map(a => a.id));

    // Cleanup removed
    Object.keys(markersRef.current).forEach(id => {
      if (!currentIds.has(id)) {
        map.removeLayer(markersRef.current[id]);
        delete markersRef.current[id];
      }
    });
    Object.keys(trailsRef.current).forEach(id => {
      if (!currentIds.has(id)) {
        map.removeLayer(trailsRef.current[id]);
        delete trailsRef.current[id];
        delete trailPointsRef.current[id];
      }
    });

    alerts.forEach(alert => {
      if (alert.latitude == null || alert.longitude == null) return;
      const lat = Number(alert.latitude);
      const lng = Number(alert.longitude);
      if (Number.isNaN(lat) || Number.isNaN(lng)) return;

      const isSelected = selectedAlert?.id === alert.id;
      const isPending = alert.status === 'pending';
      const isLive = !!liveLocations[alert.id];
      const color = alert.category_color || '#EF4444';

      // Trail
      if (!trailPointsRef.current[alert.id]) trailPointsRef.current[alert.id] = [];
      const trail = trailPointsRef.current[alert.id];
      const lastPoint = trail[trail.length - 1];
      if (!lastPoint || lastPoint[0] !== lat || lastPoint[1] !== lng) {
        trail.push([lat, lng]);
        if (trail.length > 200) trail.shift();
      }

      if (trail.length >= 2) {
        if (trailsRef.current[alert.id]) {
          trailsRef.current[alert.id].setLatLngs(trail);
        } else {
          trailsRef.current[alert.id] = L.polyline(trail, {
            color: color, weight: 3, opacity: 0.6, dashArray: '6, 8'
          }).addTo(map);
        }
      }

      const liveRing = isLive
        ? `<div style="
            position:absolute;top:50%;left:50%;
            width:${isSelected?60:50}px;height:${isSelected?60:50}px;
            border:2px solid ${color};border-radius:50%;
            transform:translate(-50%,-50%);
            animation:live-ping 1.5s ease-out infinite;
          "></div>`
        : '';

      const icon = L.divIcon({
        className: 'custom-marker',
        html: `
          <div style="position:relative;width:${isSelected?40:30}px;height:${isSelected?40:30}px;">
            ${liveRing}
            <div style="
              position:absolute;top:0;left:0;
              width:${isSelected?40:30}px;height:${isSelected?40:30}px;
              background:${color};
              border:3px solid ${isSelected?'#fff':'rgba(255,255,255,0.4)'};
              border-radius:50%;
              display:flex;align-items:center;justify-content:center;
              font-size:${isSelected?18:14}px;
              box-shadow:0 0 ${isPending?'20':'10'}px ${color}80;
              cursor:pointer;
              ${isPending?'animation:marker-pulse 2s ease-in-out infinite;':''}
            ">
              ${alert.category_icon || '🆘'}
            </div>
          </div>
          <style>
            @keyframes marker-pulse {
              0%,100% { box-shadow:0 0 10px ${color}40; }
              50% { box-shadow:0 0 25px ${color}80; }
            }
            @keyframes live-ping {
              0% { transform:translate(-50%,-50%) scale(0.6); opacity:1; }
              100% { transform:translate(-50%,-50%) scale(1.8); opacity:0; }
            }
          </style>
        `,
        iconSize: [isSelected ? 40 : 30, isSelected ? 40 : 30],
        iconAnchor: [isSelected ? 20 : 15, isSelected ? 20 : 15],
      });

      if (markersRef.current[alert.id]) {
        markersRef.current[alert.id].setLatLng([lat, lng]);
        markersRef.current[alert.id].setIcon(icon);
      } else {
        const marker = L.marker([lat, lng], { icon })
          .addTo(map)
          .on('click', () => onSelectAlert(alert));

        marker.bindTooltip(
          `<div style="font-family:Inter,sans-serif;font-size:12px;">
            <strong>${alert.category_icon} ${alert.category_name}</strong><br/>
            ${alert.user_name} • ${alert.status.toUpperCase()}
          </div>`,
          { direction: 'top', offset: [0, -20] }
        );

        markersRef.current[alert.id] = marker;
      }
    });

    // Auto-fly to selected alert
    if (selectedAlert?.latitude != null && selectedAlert?.longitude != null) {
      const lat = Number(selectedAlert.latitude);
      const lng = Number(selectedAlert.longitude);
      if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
        map.flyTo([lat, lng], 16, { duration: 0.8 });
      }
    }
    // Or auto-fit to all markers if nothing selected
    else if (alerts.length > 0) {
      const validAlerts = alerts.filter(a => a.latitude != null && a.longitude != null);
      if (validAlerts.length === 1) {
        map.flyTo([Number(validAlerts[0].latitude), Number(validAlerts[0].longitude)], 14, { duration: 0.6 });
      } else if (validAlerts.length > 1) {
        const bounds = L.latLngBounds(validAlerts.map(a => [Number(a.latitude), Number(a.longitude)]));
        map.fitBounds(bounds, { padding: [60, 60], maxZoom: 14 });
      }
    }
  }, [alerts, selectedAlert, onSelectAlert, liveLocations, leafletReady]);

  const recenterOnSelected = () => {
    if (!selectedAlert || !mapInstance.current) return;
    const lat = Number(selectedAlert.latitude);
    const lng = Number(selectedAlert.longitude);
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
      mapInstance.current.flyTo([lat, lng], 17, { duration: 0.6 });
    }
  };

  return (
    <div style={{
      position: fullscreen ? 'fixed' : 'relative',
      top: fullscreen ? 0 : 'auto',
      left: fullscreen ? 0 : 'auto',
      width: fullscreen ? '100vw' : '100%',
      height: fullscreen ? '100vh' : '100%',
      zIndex: fullscreen ? 9999 : 'auto',
      background: 'var(--bg-primary)'
    }}>
      <div ref={mapRef} className="map-container"
           style={{ width: '100%', height: '100%' }} />

      {/* Loading state */}
      {!leafletReady && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          color: 'var(--text-muted)', fontSize: 13,
          background: 'var(--bg-primary)'
        }}>
          Loading map…
        </div>
      )}

      {/* Map controls overlay */}
      <div style={{
        position: 'absolute', top: 12, left: 12, display: 'flex',
        gap: 8, zIndex: 1000
      }}>
        <button
          onClick={() => setFullscreen(!fullscreen)}
          title={fullscreen ? 'Exit fullscreen' : 'Fullscreen map'}
          style={mapBtn}
        >
          {fullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
        </button>
        {selectedAlert && (
          <button onClick={recenterOnSelected} title="Re-center on alert" style={mapBtn}>
            <Crosshair size={16} />
          </button>
        )}
      </div>

      {/* Status pill in fullscreen */}
      {fullscreen && (
        <div style={{
          position: 'absolute', top: 12, right: 60,
          background: 'rgba(10,14,26,0.9)', color: 'var(--text-primary)',
          padding: '6px 14px', borderRadius: 20, fontSize: 12,
          fontFamily: 'var(--font-display)', fontWeight: 700,
          border: '1px solid var(--border)', zIndex: 1000
        }}>
          {alerts.length} ALERT{alerts.length !== 1 ? 'S' : ''} • PRESS ESC OR CLICK ⛶ TO EXIT
        </div>
      )}
    </div>
  );
}

const mapBtn = {
  width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'rgba(10,14,26,0.9)', border: '1px solid var(--border)',
  borderRadius: 8, color: 'var(--text-primary)', cursor: 'pointer',
  backdropFilter: 'blur(8px)'
};
